import { buildCsvDocument } from './csvUtils';
import {
  formatLohnartDisplay,
  formatPayrollAdjustmentDate,
  type PayrollAdjustmentCategory,
  type PayrollAdjustmentMonthRow,
} from './payrollAdjustmentCategories';

function formatCsvAmount(amount: number): string {
  return (Number(amount) || 0).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function signedCsvAmount(type: 'bezug' | 'abzug', amount: number): string {
  const formatted = formatCsvAmount(amount);
  return type === 'bezug' ? formatted : `-${formatted}`;
}

function categoryLabel(categories: Map<string, PayrollAdjustmentCategory>, key: string): string {
  return categories.get(key)?.label ?? key;
}

function categoryLohnart(categories: Map<string, PayrollAdjustmentCategory>, key: string): string {
  const lohnart = categories.get(key)?.lohnart ?? '';
  const display = formatLohnartDisplay(lohnart);
  return display === '—' ? '' : display;
}

function categoryType(categories: Map<string, PayrollAdjustmentCategory>, key: string): string {
  const type = categories.get(key)?.type;
  if (type === 'bezug') return 'Bezug';
  if (type === 'abzug') return 'Abzug';
  return '';
}

export function buildPayrollAdjustmentMonthCsv(
  rows: PayrollAdjustmentMonthRow[],
  categories: PayrollAdjustmentCategory[],
  yearMonth: string,
): string {
  const categoryByKey = new Map(categories.map((c) => [c.key, c]));
  return buildCsvDocument(
    ['Mitarbeiter', 'Datum', 'Kategorie', 'Art', 'Lohnart', 'Betrag', 'Notiz', 'Regelmäßig'],
    rows.map((row) => [
      row.employee_name,
      formatPayrollAdjustmentDate(row.booking_date),
      categoryLabel(categoryByKey, row.category_key),
      categoryType(categoryByKey, row.category_key),
      categoryLohnart(categoryByKey, row.category_key),
      signedCsvAmount(row.type, row.amount),
      row.note,
      row.recurring ? 'Ja' : 'Nein',
    ]),
  );
}

export function downloadCsvDocument(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function payrollAdjustmentMonthExportFilename(yearMonth: string): string {
  return `be-abzuege_${yearMonth}.csv`;
}

export function payrollAdjustmentRangeExportFilename(from: string, to: string): string {
  return `be-abzuege_${from}_${to}.csv`;
}
